import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AboutUs.css';
import { useNavigate } from 'react-router-dom';
import { FaBullseye, FaBinoculars } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);


export default function AboutUs({ onBack, onNavigateProducts }) {
  const containerRef = useRef(null);
  const navigate = useNavigate();



  useEffect(() => {
    window.scrollTo(0, 0);
    const el = containerRef.current;

    // Hero Animations
    gsap.fromTo(el.querySelectorAll('.hero-fade-in'),
      { opacity: 0, y: 35 },
      { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: 'power3.out' }
    );

    gsap.fromTo(el.querySelectorAll('.hero-stat-card'),
      { scale: 0.85, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'back.out(1.4)', delay: 0.3 }
    );

    // Who We Are Animations
    gsap.fromTo(el.querySelector('.who-image-block'),
      { x: -50, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el.querySelector('.who-section'),
          start: 'top 80%',
        }
      }
    );

    gsap.fromTo(el.querySelectorAll('.who-fade-in'),
      { x: 50, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el.querySelector('.who-section'),
          start: 'top 80%',
        }
      }
    );

    // MVV Cards Animation
    gsap.fromTo(el.querySelectorAll('.mvv-card'),
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el.querySelector('.mvv-section'),
          start: 'top 80%',
        }
      }
    );

    // Tricolor Section Animations
    gsap.fromTo(el.querySelector('.tricolor-img-frame'),
      { scale: 0.92, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.9,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el.querySelector('.tricolor-section'),
          start: 'top 78%'
        }
      }
    );

    gsap.fromTo(el.querySelectorAll('.flag-value-row'),
      { x: 35, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.7,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el.querySelector('.tricolor-section'),
          start: 'top 78%'
        }
      }
    );

    // Products Section Animations
    gsap.fromTo(el.querySelectorAll('.product-grid-card'),
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.08,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el.querySelector('.products-section'),
          start: 'top 78%'
        }
      }
    );

    // Founders Section Animations
    gsap.fromTo(el.querySelectorAll('.founder-card'),
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.13,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el.querySelector('.founders-grid'),
          start: 'top 82%'
        }
      }
    );

    // Certifications Animations
    gsap.fromTo(el.querySelectorAll('.cert-card'),
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el.querySelector('.cert-section'),
          start: 'top 80%'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  const liquidProducts = [
    {
      name: "BIO-ORBI Super Novel",
      hindi: "बायो-ऑर्ब सूपर नोवेल",
      tagline: "Banana Pseudostem Organic Input",
      img: "/products/bio-orbi-super-novel.png",
      features: [
        "Upcycles banana pseudostem waste (Fibre, Scutcher, Sap, Central Core).",
        "Government-patent-filed via ICAR (Institute of Govt. of India).",
        "Developed in technical collaboration with Navsari Agricultural University."
      ],
      benefits: [
        "Rich in natural Potassium (K), Phosphorus (P), Calcium (Ca), and Magnesium (Mg).",
        "Retains soil moisture longer, shielding roots during harsh dry weather.",
        "Provides an eco-friendly, zero-chemical, high-yield plant booster."
      ]
    },
    {
      name: "BIO-ORBI Super",
      hindi: "बायो-ऑर्ब सूपर",
      tagline: "Liquid Fermented Organic Manure",
      img: "/products/bio-orbi-super.png",
      features: [
        "Fermented via beneficial micro-organisms (yeast, fungi, bacteria).",
        "100% liquid-soluble organic matter for complete absorption.",
        "Safe for application at any stage of crop growth cycle."
      ],
      benefits: [
        "Improves crop resilience to biotic and abiotic stresses.",
        "Improves organic soil content to nurture earthworms and mycorrhizae."
      ]
    },
    {
      name: "BIO-ORBI Super-Prime",
      hindi: "बायो-ऑर्ब सूपर प्राइम",
      tagline: "Potassium Mobilizing Biofertilizer (KMB)",
      img: "/products/bio-orbi-super-prime.png",
      features: [
        "Contains high-efficiency Potassium Mobilizing Organisms (KMO).",
        "Solubilizes locked-up soil potash, making it available to crops.",
        "Releases essential organic acids that break down mineral compounds."
      ],
      benefits: [
        "Enhances crop growth, size, and shelf-life in salt-affected soils.",
        "Improves water-use efficiency and biotic/abiotic stress tolerance."
      ]
    },
    {
      name: "BIO-ORBI Prime",
      hindi: "बायो-ऑर्ब प्राइम",
      tagline: "Azotobacter Biofertilizer",
      img: "/products/bio-orbi-prime.png",
      features: [
        "Aerobic free-living nitrogen-fixing bacterium.",
        "Suitable for all non-leguminous plants (cotton, cereals, rice).",
        "Generates vital plant growth hormones (auxins, gibberellins).",
        "Suppresses harmful soil-borne pathogens."
      ],
      benefits: [
        "Enhances crop yield potentials in tomatoes, eggplants, cotton, etc.",
        "Improves long-term soil microbial structure."
      ]
    },
    {
      name: "BIO-ORBI Super-Plus",
      hindi: "ಬಯೋ-ಆರ್ಬಿಐ ಸೂಪರ್-ಪ್ಲಸ್",
      tagline: "Phosphate Solubilizing Bacteria (PSB)",
      img: "/products/bio-orbi-super-plus.png",
      features: [
        "Contains phosphate-solubilizing bacteria strains.",
        "Converts locked-up, insoluble soil phosphates into soluble phosphorus.",
        "Promotes massive root-system development and branching."
      ],
      benefits: [
        "Highly beneficial for all crops including oilseeds and pulses.",
        "Enhances uniform crop flowering, fruit setting, and overall vigour."
      ]
    },
    {
      name: "BIO-ORBI Plus",
      hindi: "बायो-ऑर्ब प्लस",
      tagline: "Rhizobium Biofertilizer",
      img: "/products/bio-orbi-plus.png",
      features: [
        "Symbiotic root-nodule forming bacterium.",
        "Specifically targets leguminous crop roots.",
        "Establishes natural nitrogen-fixation nodules directly at root sites."
      ],
      benefits: [
        "Converts atmospheric nitrogen into ammonia for protein synthesis.",
        "Enriches plants with essential vitamins and nitrogen compounds."
      ]
    }
  ];

  const solidProducts = [
    {
      name: "BIO-ORBI Organic Manure",
      hindi: "बायो-ऑर्ब आर्गेनिक मनूर",
      tagline: "Soil Restorer & Conditioner",
      img: "/products/Organic Manure.png",
      features: [
        "Rich in premium humic substances and organic carbon.",
        "Restores structural integrity and aeration of depleted soils.",
        "Increases the Cation Exchange Capacity (CEC) to hold nutrients."
      ],
      benefits: [
        "Improves water-holding capacity and counters soil compaction.",
        "Creates a long-term foundation for high-efficiency organic farming."
      ]
    },
    {
      name: "BIO-ORBI Bio Enriched Organic Manure",
      hindi: "बायो-ऑर्ब एनरिच्ड आर्गेनिक मनूर",
      tagline: "Microbial Fortified Soil Input",
      img: "/products/Enriched Organic Manure.png",
      features: [
        "Fortified with active, beneficial nitrogen-fixing and PSB cultures.",
        "Blends high-quality organic matter with live biofertilizers.",
        "Boosts natural enzyme activity in the crop rhizosphere."
      ],
      benefits: [
        "Supplies both immediate and sustained organic nourishment.",
        "Stimulates soil microflora to rapidly break down soil minerals."
      ]
    },
    {
      name: "BIO-ORBI Vermicompost",
      hindi: "बायो-ऑर्ब वर्मीकम्पोस्ट",
      tagline: "Premium Organic Worm Castings",
      img: "/products/Vermicompost.png",
      features: [
        "100% organic compost produced via scientific vermiculture.",
        "Rich in earthworm-derived enzymes, auxins, and humic acids.",
        "Contains vital trace elements in highly bio-available forms."
      ],
      benefits: [
        "Accelerates seed germination and root system proliferation.",
        "Improves crop quality, colour, taste, and shelf-life."
      ]
    }
  ];

  return (
    <div className="about-root" ref={containerRef}>
      {/* HERO SECTION */}
      <section className="about-hero">
        <div className="about-hero-bg" style={{ backgroundImage: "url('/banner/organic_fertilizer_hero.png')" }}></div>
        <div className="about-hero-overlay"></div>
        <div className="container">
          <div className="hero-inner">
            <div className="hero-left">
              <div className="hero-seal-row hero-fade-in">
                <span className="hero-seal-text">Orbifert Organic Pvt. Ltd.</span>
              </div>
              <h1 className="hero-fade-in">Reviving Soil,<br /><span>Rewarding Farmers</span></h1>
              <p className="hero-fade-in">We manufacture government-patent-filed organic fertilizers and biological crop inputs from our facility in Navsari, Gujarat — built to restore soil health and put more in the farmer's hand at harvest.</p>
              <div className="hero-btns hero-fade-in">
                <button className="btn-primary" onClick={onNavigateProducts}>View Our Products</button>
                <button className="btn-ghost" onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}>Talk to Us</button>
              </div>
            </div>
            <div className="hero-stats">
              <div className="hero-stat-card">
                <div className="num">20+</div>
                <div className="label">Years combined field & lab experience among our directors</div>
              </div>
              <div className="hero-stat-card">
                <div className="num">9</div>
                <div className="label">Bio-Orbi formulations & inputs in current product range</div>
              </div>
              <div className="hero-stat-card">
                <div className="num">1</div>
                <div className="label">Patent filed via ICAR, Government of India</div>
              </div>
              <div className="hero-stat-card">
                <div className="num">100%</div>
                <div className="label">Organic — zero synthetic chemicals, zero heavy metals</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE SECTION */}
      <section className="who-section">
        <div className="container">
          <div className="who-grid">
            <div className="who-image-block">
              <div className="who-main-img-container">
                <img
                  src="/banner/farm-field-about.png"
                  alt="Organic agricultural farming crops field"
                  className="who-main-img"
                />
              </div>
              <div className="who-badge">
                <div className="badge-icon-circle">🇮🇳</div>
                <div className="badge-text">
                  <strong>Make In India</strong>
                  <span>Govt. Registered Venture</span>
                </div>
              </div>
            </div>
            <div className="who-content">
              <span className="eyebrow who-fade-in">Who We Are</span>
              <h2 className="section-title who-fade-in">A company built <span>from the soil up</span></h2>
              <p className="section-body who-fade-in">Orbifert Organic Pvt. Ltd. manufactures nutrient-rich organic fertilizers, compost, and specialized biological crop inputs in user-friendly liquid and spray formats — engineered for seamless soil revival and bumper harvests, without a single synthetic chemical in the formulation.</p>
              <ul className="check-list who-fade-in">
                <li>
                  <span className="check-dot">✓</span>
                  <span>Government-patent filed through ICAR (Indian Council of Agricultural Research), Govt. of India.</span>
                </li>
                <li>
                  <span className="check-dot">✓</span>
                  <span>Official Make in India registration and supported by WIPO guidelines.</span>
                </li>
                <li>
                  <span className="check-dot">✓</span>
                  <span>Formulations developed with Navsari Agricultural University.</span>
                </li>
                <li>
                  <span className="check-dot">✓</span>
                  <span>Led by directors with 20+ years in agriculture, horticulture, and biotechnology.</span>
                </li>
                <li>
                  <span className="check-dot">✓</span>
                  <span>Our core mission is to empower farmers, create rural employment, and protect the planet.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION SECTION */}
      <section className="mvv-section">
        <div className="container">
          <div className="mvv-grid">
            <div className="mvv-card mission">
              <div className="mvv-icon-container">
                <FaBullseye />
              </div>
              <div className="mvv-label">Our Mission</div>
              <h3>Nutrient-rich soil, in every farmer's hands</h3>
              <p>To manufacture nutrient-rich organic fertilizers and compost, empower farmers, create employment, revive soil health, and protect the planet through sustainable farming. Come join the purpose.</p>
            </div>
            <div className="mvv-card vision">
              <div className="mvv-icon-container">
                <FaBinoculars />
              </div>
              <div className="mvv-label">Our Vision</div>
              <h3>Pioneering the organic revolution</h3>
              <p>To pioneer the organic revolution for a healthier, more sustainable evolution in Indian agriculture — where soil health and farmer income grow together, not at each other's expense.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRICOLOR VALUES SECTION */}
      <section className="tricolor-section">
        <div className="container">
          <div className="tricolor-grid">
            <div className="tricolor-img-frame">
              <img src="/banner/seedling-growth-about.png" alt="Healthy sprouting crop" />
              <div className="tricolor-overlay-text">
                <strong>Navsari, Gujarat</strong>
                <span>Where every batch is formulated and tested</span>
              </div>
            </div>
            <div className="tricolor-content">
              <span className="eyebrow">Our Foundation</span>
              <h2 className="section-title">Values borrowed from the <span>tricolor itself</span></h2>
              <p className="section-body">Orbifert's beliefs reflect the values represented in the Indian flag — and we carry them forward through every batch we manufacture.</p>

              <div className="flag-values">
                <div className="flag-value-row">
                  <span className="flag-swatch saffron"></span>
                  <div className="flag-value-text">
                    <h5>Saffron — Courage &amp; Strength</h5>
                    <p>The courage to commit to 100% organic formulations even where chemical shortcuts are cheaper and faster.</p>
                  </div>
                </div>
                <div className="flag-value-row">
                  <span className="flag-swatch white-sw"></span>
                  <div className="flag-value-text">
                    <h5>White — Peace &amp; Truth</h5>
                    <p>Honest labeling and honest science — what's on the bottle is what's in the bottle.</p>
                  </div>
                </div>
                <div className="flag-value-row">
                  <span className="flag-swatch green-sw"></span>
                  <div className="flag-value-text">
                    <h5>Green — Growth &amp; Prosperity</h5>
                    <p>Growth measured in soil health and farmer income, not just litres sold.</p>
                  </div>
                </div>
                <div className="flag-value-row">
                  <span className="flag-swatch navy-sw"></span>
                  <div className="flag-value-text">
                    <h5>The Ashoka Chakra — Moving Forward</h5>
                    <p>Progress through research — every formulation is tested in real fields before it reaches a farmer.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT RANGE SHOWCASE */}
      <section className="products-section">
        <div className="container">
          <div className="center-header">
            <span className="eyebrow">Product range</span>
            <h2 className="section-title">Complete <span>Bio-Orbi Portfolio</span></h2>
            <p className="section-body">Our bio-fertilizers and organic conditioners are scientifically formulated to nurture crops naturally and rebuild soil biology.</p>
          </div>

          {/* Liquid Series */}
          <div className="product-category-wrap">
            <h3 className="category-title"><span>Bio-Orbi Liquid Series</span> — Bio-Fertilizers</h3>
            <div className="products-grid">
              {liquidProducts.map((p, i) => (
                <div key={i} className="product-grid-card">
                  <div className="product-card-head">
                    <span className="product-index">0{i + 1}</span>
                    <div className="product-img-wrap">
                      <img src={p.img} alt={p.name} className="product-img" />
                    </div>
                  </div>
                  <div className="product-card-body">
                    <h4>{p.name}</h4>
                    <span className="product-hindi">{p.hindi}</span>
                    <span className="product-tagline">{p.tagline}</span>

                    <div className="product-details-block">
                      <h5>Key Features</h5>
                      <ul className="product-feature-list">
                        {p.features.map((f, idx) => <li key={idx}>{f}</li>)}
                      </ul>
                      <h5>Key Benefits</h5>
                      <ul className="product-benefit-list">
                        {p.benefits.map((b, idx) => <li key={idx}>{b}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solid Series */}
          <div className="product-category-wrap" style={{ marginTop: '80px' }}>
            <h3 className="category-title"><span>Bio-Orbi Solid Series</span> — Soil Conditioners</h3>
            <div className="products-grid">
              {solidProducts.map((p, i) => (
                <div key={i} className="product-grid-card">
                  <div className="product-card-head">
                    <span className="product-index">0{i + 7}</span>
                    <div className="product-img-wrap">
                      <img src={p.img} alt={p.name} className="product-img" />
                    </div>
                  </div>
                  <div className="product-card-body">
                    <h4>{p.name}</h4>
                    <span className="product-hindi">{p.hindi}</span>
                    <span className="product-tagline">{p.tagline}</span>

                    <div className="product-details-block">
                      <h5>Key Features</h5>
                      <ul className="product-feature-list">
                        {p.features.map((f, idx) => <li key={idx}>{f}</li>)}
                      </ul>
                      <h5>Key Benefits</h5>
                      <ul className="product-benefit-list">
                        {p.benefits.map((b, idx) => <li key={idx}>{b}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDERS SECTION */}
      <section className="founders-section" style={{ background: "linear-gradient(rgba(54, 59, 109, 0.88), rgba(54, 59, 109, 0.88)), url('/logo/Orbifert-universe.png') no-repeat center center / cover" }}>
        <div className="container">
          <span className="eyebrow">About the Founders</span>
          <h2 className="section-title text-white" style={{ color: '#fff', maxWidth: '600px', marginBottom: '50px' }}>Three disciplines, one purpose: <span>better soil, better harvests.</span></h2>
          <div className="founders-grid">
            <div className="founder-card">
              <div className="founder-index-circle">01</div>
              <h4>Jamsheed Vachha</h4>
              <div className="founder-role">Director</div>
              <p>With years of experience in the business field, he excels in strategic planning, leadership, and entrepreneurship. He drives Orbifert Organic’s overall growth. His financial acumen ensures sustainable growth and compliance.</p>
            </div>
            <div className="founder-card">
              <div className="founder-index-circle">02</div>
              <h4>Niraj Naik</h4>
              <div className="founder-role">Director</div>
              <p>With 20+ years in agriculture and a degree in horticulture, he ensures Orbifert’s excellence. An expert in quality control and technical lab operations, he perfects our fertilisers. His vast knowledge and awareness in soil quality and plant health has helped in maneuvering from research to practical applications in the fields of organic farming.</p>
            </div>
            <div className="founder-card">
              <div className="founder-index-circle">03</div>
              <h4>Eramnaaz Shaikh</h4>
              <div className="founder-role">Director</div>
              <p>A skilled microbiologist with expertise in biotechnology, she powers Orbifert’s innovations. Her research ensures microbial soil health. Cultivating breakthroughs for better soil today and better harvests tomorrow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS SECTION */}
      <section className="cert-section">
        <div className="container">
          <div className="center-header">
            <span className="eyebrow">Trust &amp; Compliance</span>
            <h2 className="section-title">Backed by the <span>institutions that matter</span></h2>
            <p className="section-body">Our formulations, quality benchmarks, and processes are verified and certified by national standards.</p>
          </div>
          <div className="cert-grid">
            <div className="cert-card">
              <div className="cert-icon-img-wrap">
                <img src="/logo/NPOP Certified.png" alt="NPOP Certified Logo" className="cert-logo-img" />
              </div>
              <h4>NPOP Certified</h4>
              <p>National Programme for Organic Production — India's standards for organic production</p>
            </div>
            <div className="cert-card">
              <div className="cert-icon-img-wrap">
                <img src="/logo/ISO 9001-2015.png" alt="ISO 9001-2015 Logo" className="cert-logo-img" />
              </div>
              <h4>ISO 9001:2015</h4>
              <p>International quality management standards across manufacturing and testing phases</p>
            </div>
            <div className="cert-card">
              <div className="cert-icon-img-wrap">
                <img src="/logo/ICAR Approved.png" alt="ICAR Approved Logo" className="cert-logo-img" />
              </div>
              <h4>ICAR Approved</h4>
              <p>Product formulations reviewed and patent filed under ICAR frameworks</p>
            </div>
            <div className="cert-card">
              <div className="cert-icon-img-wrap">
                <img src="/logo/Eco-Packaging.png" alt="Eco-Packaging Logo" className="cert-logo-img" />
              </div>
              <h4>Make in India</h4>
              <p>Officially registered under the national Make in India initiative</p>
            </div>
            <div className="cert-card">
              <div className="cert-icon-img-wrap">
                <img src="/logo/logo_632abd28aea16.png" alt="Navsari Agricultural University Logo" className="cert-logo-img" />
              </div>
              <h4>NAU Collaborated</h4>
              <p>Developed in technical collaboration and research partnerships with Navsari Agricultural University</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section">
        <div className="cta-bg" style={{ backgroundImage: "url('/banner/organic_fertilizer_hero.png')" }}></div>
        <div className="cta-overlay"></div>
        <div className="container">
          <span className="eyebrow" style={{ color: '#f1921d' }}>Get in Touch</span>
          <h2>Ready to revive your soil?</h2>
          <p>Talk to our team about which Bio-Orbi formulation fits your crop, soil type, and season — backed by 20+ years of field experience and a government-filed patent.</p>
          <div className="cta-btns">
            <button className="btn-primary" onClick={onNavigateProducts}>Shop Now</button>
            <button className="btn-ghost" onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }} style={{ color: '#fff' }}>Contact Orbifert</button>
          </div>

          <div className="cta-office-details">
            <div className="office-detail-row">
              <strong>Corporate Office:</strong>
              <span>Shop No-3 Neel Complex, Sisodara Rd., Italva, Navsari-396445 Gujarat, India</span>
            </div>
            <div className="office-detail-row">
              <strong>Manufacturing Unit:</strong>
              <span>L-S No. 237-A2, Vedchha-Mohanpur Ancheli Road, Gandevi, Mohanpur, District: Navsari, Gujarat – 396310</span>
            </div>
            <div className="office-detail-row-grid">
              <div>
                <strong>Customer Care:</strong>
                <span>+91 7228064418</span>
              </div>
              <div>
                <strong>Email us:</strong>
                <span>orbifertorganic@gmail.com</span>
              </div>
              <div>
                <strong>Website:</strong>
                <span>www.orbifertorganic.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
